import {
  createFileRoute,
  Link,
  Outlet,
  useCanGoBack,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import {
  Banknote,
  BookUser,
  CalendarDays,
  CircleArrowLeft,
  ClipboardList,
  GraduationCap,
  LayoutGrid,
  Upload,
  User,
  Users,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { useGetStudentById } from '~entities/student';
import { getStudentByIdOptions } from '~entities/student/api/get-student-by-id';
import { useUploadStudentProfilePictureMutation } from '~entities/student/api/upload-student-profile-picture';
import { Gender } from '~entities/student/modal/student';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { FamilyHubLinkButton } from '~features/family-hub/ui/family-hub-link-button';

import { TabList, type TabListItem } from '~shared/components/tab-list';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { useIsMobile } from '~shared/shadcn/hooks/use-mobile';
import { cn } from '~shared/shadcn/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '~shared/shadcn/ui/avatar';
import { Button } from '~shared/shadcn/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~shared/shadcn/ui/sheet';
import { Skeleton } from '~shared/shadcn/ui/skeleton';

function getTabsList(
  studentId: string,
  pathname: string,
  includePaymentsTab: boolean,
): Array<TabListItem> {
  const detailPathPattern = /\/students\/[a-zA-Z0-9]+\/details/;
  const contactsPathPattern = /\/students\/[a-zA-Z0-9]+\/contacts/;
  // const paymentsPathPattern = /\/students\/[a-zA-Z0-9]+\/payments/;
  const newPaymentsPathPattern = /\/students\/[a-zA-Z0-9]+\/new-payments/;
  const notesPathPattern = /\/students\/[a-zA-Z0-9]+\/notes/;
  const classesPathPattern = /\/students\/[a-zA-Z0-9]+\/classes/;
  const makeupsPathPattern = /\/students\/[a-zA-Z0-9]+\/makeups/;
  const policiesPathPattern = /\/students\/[a-zA-Z0-9]+\/policies/;

  const tabs: Array<TabListItem> = [
    {
      href: `/students/${studentId}/details`,
      label: 'Details',
      active: detailPathPattern.test(pathname),
      icon: User,
    },
    {
      href: `/students/${studentId}/classes`,
      label: 'Classes & Events',
      active: classesPathPattern.test(pathname),
      icon: GraduationCap,
    },
    {
      href: `/students/${studentId}/makeups`,
      label: 'Makeups',
      active: makeupsPathPattern.test(pathname),
      icon: CalendarDays,
    },
    {
      href: `/students/${studentId}/contacts`,
      label: 'Contacts',
      active: contactsPathPattern.test(pathname),
      icon: BookUser,
    },
  ];

  // ENR-3529: Staff cannot open student Payments (Admin+ only).
  if (includePaymentsTab) {
    tabs.push({
      href: `/students/${studentId}/new-payments`,
      label: 'Payments',
      active: newPaymentsPathPattern.test(pathname),
      icon: Banknote,
    });
  }

  tabs.push(
    {
      href: `/students/${studentId}/notes`,
      label: 'Notes',
      active: notesPathPattern.test(pathname),
      icon: ClipboardList,
    },
    {
      href: `/students/${studentId}/policies`,
      label: 'Policies',
      active: policiesPathPattern.test(pathname),
      icon: ClipboardList,
    },
  );

  return tabs;
}

const Component = () => {
  const { pathname } = useLocation();
  const { studentId } = Route.useParams();
  const router = useRouter();
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);
  const { hasAdminAccess, isRolesReady } = useCurrentUserRole();

  const canGoBack = useCanGoBack();

  const tabsList = getTabsList(
    studentId,
    pathname,
    isRolesReady && hasAdminAccess,
  );

  const { data, isPending } = useGetStudentById(studentId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadProfilePicture } =
    useUploadStudentProfilePictureMutation();

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error('File Upload Error', {
          description:
            'File size exceeds 10MB limit. Please choose a smaller file.',
        });

        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file type
      const fileType = file.type.split('/')[1];
      const ALLOWED_TYPES = ['jpeg', 'png', 'gif', 'webp', 'jpg'];

      if (!ALLOWED_TYPES.includes(fileType?.toLowerCase() ?? '')) {
        toast.error('Invalid File Type', {
          description: 'Please upload an image (JPEG, PNG, GIF, WebP)',
        });
        return;
      }

      if (fileType) {
        uploadProfilePicture({
          studentId,
          fileType,
          file,
        });
      }
    }
  };

  // ENR-2522: keep chrome while first paint waits; never wipe to bare "Loading...".
  // Route loader + intent preload usually hydrate before this renders.
  if (isPending && !data) {
    return <StudentAccountShellSkeleton />;
  }

  if (!data) {
    return <div>Student not found</div>;
  }

  const canViewFamily = isRolesReady && hasAdminAccess;
  const familyButtonLabel = data.familyName?.trim()
    ? `View ${data.familyName.trim()} family`
    : 'View family';

  const mobileActionsButtonClass =
    'group h-11 w-full gap-2.5 whitespace-nowrap rounded-xl border-border/60 bg-white px-5 font-medium transition-all duration-150 hover:scale-[1.01] hover:border-border hover:bg-[#F9FAFB] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] active:scale-[0.985]';

  return (
    <>
      <div className='mb-4 flex items-center gap-2'>
        {canGoBack ? (
          <Button
            variant={'ghost'}
            className='flex items-center p-0 [&_svg]:size-6'
            onClick={() => {
              router.history.back();
            }}
          >
            <CircleArrowLeft className='size-6' />
          </Button>
        ) : (
          <Button
            variant={'ghost'}
            className='flex items-center p-0 [&_svg]:size-6'
            asChild
          >
            <Link to='/dashboard'>
              <CircleArrowLeft className='size-6' />
            </Link>
          </Button>
        )}
        <h2 className='text-2xl font-bold text-gray-800'>Account</h2>
      </div>
      <div className='relative -mx-4 h-32 bg-gradient-to-b from-[#0369A1CC] to-[#003566CC] sm:h-40 lg:-mx-10'>
        <div className="absolute inset-0 z-0 bg-[url('/background-texture.png')] bg-cover bg-no-repeat opacity-20"></div>
        <div className='absolute left-4 top-16 size-24 sm:left-10 sm:top-[4.563rem] sm:size-40'>
          <Avatar className='group relative size-24 sm:size-40'>
            {data.profilePicUrl ? (
              <AvatarImage
                src={data.profilePicUrl}
                className={cn(
                  'rounded-full border-4 border-custom-secondary-1 bg-white',
                )}
              />
            ) : (
              <AvatarImage
                src={
                  data.gender === Gender.Male
                    ? '/male.png'
                    : data.gender === Gender.Female
                      ? '/female.png'
                      : '/other.png'
                }
                className={cn(
                  'rounded-full border-4 border-custom-secondary-1 bg-white',
                )}
              />
            )}
            <AvatarFallback>
              <User size={100} />
            </AvatarFallback>
            <button
              onClick={handleButtonClick}
              className='absolute inset-0 z-10 hidden items-center justify-center rounded-full border border-gray-300 bg-white p-2 opacity-0 transition-opacity duration-300 group-hover:flex group-hover:opacity-80'
            >
              <Upload className='size-5 text-black' />
            </button>
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              className='hidden'
            />
          </Avatar>
        </div>
      </div>
      <div className='px-4 sm:px-10'>
        <h1 className='mt-16 text-2xl font-semibold leading-9 sm:mt-[6.563rem] sm:text-4xl'>
          {data.name}
        </h1>
        <p className='mt-2 text-black/[0.6]'>
          {formatDateOnlySafe(data.dob, 'PPP')}
        </p>
      </div>
      {isMobile ? (
        <div className='mt-8 px-4'>
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button
                type='button'
                variant='outline'
                className={mobileActionsButtonClass}
              >
                <LayoutGrid className='size-4' />
                More Actions
              </Button>
            </SheetTrigger>
            <SheetContent side='right' className='w-[300px] overflow-y-auto'>
              <SheetHeader>
                <SheetTitle>More Actions</SheetTitle>
                <SheetDescription>
                  Jump to another section for this student.
                </SheetDescription>
              </SheetHeader>
              <nav className='mt-6 flex flex-col gap-2'>
                {canViewFamily ? (
                  <FamilyHubLinkButton
                    studentId={studentId}
                    parentId={data.parentId}
                    label={familyButtonLabel}
                    variant='outline'
                    className='w-full justify-start'
                    onNavigate={() => {
                      setNavOpen(false);
                    }}
                  >
                    <Users className='mr-2 size-4' />
                    {familyButtonLabel}
                  </FamilyHubLinkButton>
                ) : null}
                {tabsList.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <Button
                      key={tab.href}
                      variant={tab.active ? 'secondary' : 'outline'}
                      className='w-full justify-start'
                      asChild
                    >
                      <Link
                        to={tab.href}
                        replace
                        onClick={() => {
                          setNavOpen(false);
                        }}
                      >
                        <TabIcon className='mr-2 size-4' />
                        {tab.label}
                      </Link>
                    </Button>
                  );
                })}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      ) : (
        <div className='mt-8'>
          <TabList tabList={tabsList} />
        </div>
      )}
      <div className='m-2 md:m-4 lg:mt-8'>
        <Outlet />
      </div>
    </>
  );
};

function StudentAccountShellSkeleton() {
  return (
    <>
      <div className='mb-4 flex items-center gap-2'>
        <Skeleton className='size-6 rounded-full' />
        <h2 className='text-2xl font-bold text-gray-800'>Account</h2>
      </div>
      <div className='relative -mx-4 h-32 bg-gradient-to-b from-[#0369A1CC] to-[#003566CC] sm:h-40 lg:-mx-10'>
        <div className="absolute inset-0 z-0 bg-[url('/background-texture.png')] bg-cover bg-no-repeat opacity-20" />
        <div className='absolute left-4 top-16 size-24 sm:left-10 sm:top-[4.563rem] sm:size-40'>
          <Skeleton className='size-24 rounded-full border-4 border-custom-secondary-1 sm:size-40' />
        </div>
      </div>
      <div className='px-4 sm:px-10'>
        <Skeleton className='mt-16 h-9 w-64 sm:mt-[6.563rem]' />
        <Skeleton className='mt-3 h-5 w-40' />
      </div>
      <div className='mt-8 px-4 sm:px-10'>
        <Skeleton className='h-10 w-full max-w-3xl' />
      </div>
      <div className='m-2 md:m-4 lg:mt-8'>
        <Skeleton className='h-64 w-full max-w-3xl' />
      </div>
    </>
  );
}

export const Route = createFileRoute('/_auth/_layout/students/$studentId')({
  component: Component,
  loader: async ({ params, preload }) => {
    // ENR-2522 Scenario A: hydrate student before swap so opening a new
    // student does not flash a blank Loading screen (intent preload + await).
    await queryClient.ensureQueryData(getStudentByIdOptions(params.studentId));
    return {
      maxAge: preload ? 5 * 60 * 1000 : 0,
    };
  },
});
